import { useEffect, useState } from 'react';
import { useRxDatabase } from 'rxdb/plugins/react';
import {
    entityConfigs,
    historyLabel,
    PERSON_FIELDS,
    personFieldIds,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    type PersonField,
    type ProjectDocType
} from '@ast-data/shared';
import { historyRecordPath } from '../history/historyRecordPath';
import { planumRefLabel, profileRefLabel } from '../findings/findingRefLabels';
import { projectMeasureNumber } from '../projects/projectTitle';
import { formatDate, toDayString } from '../../utils/formatDate';

/**
 * Everything one account is linked to, across every kind of record: the
 * person fields (Ausgräber, Fotograf, Zeichner, Autor, Mitarbeiter …), the
 * projects it is assigned to (directly or through a Team) and the Teams it
 * belongs to.
 *
 * By the account LINK (the `*Id` beside a name), never by the name: a link is
 * the identity every comparison in the app runs on, and a name may be spelt
 * two ways. Read from the local database — reads are global, so every
 * record is here.
 */

/** One link: a record, and in what way the account is named on it. */
export interface UserLinkRow {
    /** Unique per record and way of linking. */
    id: string;
    /** `entity.name` — what kind of record. */
    entityType: string;
    recordId: string;
    /** i18n id of the field (or relation) that names the account. */
    kindId: string;
    /** What names the record, as the change log does. */
    label: string;
    /** The project's Maßnahmennummer (or short title), '' without one. */
    project: string;
    /** 'YYYY-MM-DD' — the record's day, '' where it has none. */
    day: string;
    /** The office route to the record, or null when it has no screen. */
    path: string | null;
}

/** The label of each person field — the one its form shows. */
const PERSON_FIELD_LABEL_IDS: Record<string, Record<string, string>> = {
    project: {
        scientificDirector: 'field.scientificDirector',
        technicalDirector: 'field.technicalDirector',
        technicalAuthor: 'field.technicalAuthor',
        scientificAuthor: 'field.scientificAuthor'
    },
    diary: {
        author: 'diary.field.author',
        excavationLeader: 'diary.field.excavationLeader',
        technician: 'diary.field.technician',
        staff: 'diary.field.staff'
    },
    find: { excavator: 'find.field.excavator' },
    sample: { sampler: 'sample.field.sampler' },
    photo: { photographer: 'photo.field.photographer' },
    drawingSheet: { drafter: 'drawingSheet.field.drafter' },
    findingPlanum: { editor: 'planum.field.editor' },
    findingProfile: { editor: 'profile.field.editor' },
    overallDescription: { editor: 'overallDescription.field.editor' },
    areaDescription: { editor: 'areaDescription.field.editor' },
    timeEntry: { workerName: 'timeEntry.field.workerName' }
};

/** The label id of a person field, as its form shows it. */
export function personFieldLabelId(entityType: string, field: string): string {
    return PERSON_FIELD_LABEL_IDS[entityType]?.[field] ?? `field.${field}`;
}

/** Links that are not person fields: membership arrays of account ids. */
const MEMBERSHIPS: Record<string, { field: string; kindId: string }> = {
    project: { field: 'userIds', kindId: 'admin.users.links.projectMember' },
    group: { field: 'userIds', kindId: 'admin.users.links.teamMember' }
};

export type LinkedDoc = Record<string, unknown> & { id: string };
type Doc = LinkedDoc;

interface Source {
    entityType: string;
    collection: string;
    fields: readonly PersonField[];
    membership?: { field: string; kindId: string };
}

/** Every collection that can name an account, with how it does. */
const SOURCES: Source[] = entityConfigs.flatMap((config) => {
    const fields = PERSON_FIELDS[config.name] ?? [];
    const membership = MEMBERSHIPS[config.name];
    if (fields.length === 0 && !membership) {
        return [];
    }
    return [
        {
            entityType: config.name,
            collection: config.collection,
            fields,
            membership
        }
    ];
});

/**
 * The query for one source. A plain equality matches an id field holding the
 * id AND an id array containing it — Mongo's rule, which RxDB follows.
 */
function selectorFor(source: Source, userId: string) {
    const clauses = [
        ...source.fields.map((field) => ({ [field.id]: userId })),
        ...(source.membership ? [{ [source.membership.field]: userId }] : [])
    ];
    return clauses.length === 1 ? clauses[0] : { $or: clauses };
}

/** In which ways a record names the account: one label id each. */
function kindsOf(source: Source, doc: Doc, userId: string): string[] {
    const kinds = source.fields
        .filter((field) =>
            personFieldIds(field.shape, doc[field.id]).includes(userId)
        )
        .map((field) => personFieldLabelId(source.entityType, field.name));
    const members = source.membership
        ? doc[source.membership.field]
        : undefined;
    if (
        source.membership &&
        Array.isArray(members) &&
        members.includes(userId)
    ) {
        kinds.push(source.membership.kindId);
    }
    return kinds;
}

/** The day that identifies a record: its Aufnahmedatum, or a work day. */
function dayOf(doc: Doc): string {
    return toDayString(doc.recordedAt ?? doc.date ?? '');
}

interface ExecutableCollection {
    find: (query: unknown) => {
        exec: () => Promise<{ toJSON: () => unknown }[]>;
    };
}

type Collections = Record<string, ExecutableCollection | undefined>;

async function read(
    collections: Collections,
    name: string,
    selector: unknown
): Promise<Doc[]> {
    const collection = collections[name];
    if (!collection) {
        return [];
    }
    const docs = await collection.find({ selector }).exec();
    return docs.map((doc) => doc.toJSON() as Doc);
}

/**
 * Every link, read ONCE. Not a live query: these are scans over whole
 * collections (an account id is not indexed), and the Zeiterfassung alone
 * runs to thousands of rows — kept live, every synced write anywhere would
 * redo them while the dialog is open. Reopening reads afresh.
 */
async function readLinks(
    collections: Collections,
    userId: string
): Promise<UserLinkRow[]> {
    const bySource = new Map<string, Doc[]>(
        await Promise.all(
            SOURCES.map(
                async (source) =>
                    [
                        source.entityType,
                        await read(
                            collections,
                            source.collection,
                            selectorFor(source, userId)
                        )
                    ] as const
            )
        )
    );
    // A project reached through a Team is the person's project as much as a
    // direct assignment (see defineAbilityFor) — listed as such.
    const teamIds = (bySource.get('group') ?? []).map((team) => team.id);
    const throughTeams = teamIds.length
        ? await read(collections, 'projects', { groupIds: { $in: teamIds } })
        : [];
    const findingIds = [
        ...new Set(
            ['findingPlanum', 'findingProfile'].flatMap((type) =>
                (bySource.get(type) ?? []).map((doc) =>
                    String(doc.findingId ?? '')
                )
            )
        )
    ].filter(Boolean);
    const findings = findingIds.length
        ? await read(collections, 'findings', { id: { $in: findingIds } })
        : [];
    const projects = await read(collections, 'projects', {});

    const findingNumber = new Map(
        findings.map((doc) => [doc.id, String(doc.findingNumber ?? '')])
    );
    const projectName = new Map(
        projects.map((doc) => {
            const project = doc as unknown as ProjectDocType;
            return [
                doc.id,
                projectMeasureNumber(project) || project.shortTitle || ''
            ];
        })
    );

    const row = linkRowBuilder(findingNumber, projectName);

    const rows = SOURCES.flatMap((source) =>
        (bySource.get(source.entityType) ?? []).flatMap((doc) =>
            kindsOf(source, doc, userId).map((kindId) =>
                row(source.entityType, doc, kindId)
            )
        )
    );
    for (const project of throughTeams) {
        rows.push(row('project', project, 'admin.users.links.projectViaTeam'));
    }
    return rows;
}

export interface UserLinks {
    rows: UserLinkRow[];
    /** The links of the account asked for have been read. */
    ready: boolean;
}

/** The records `userId` is linked to; nothing while no id is given. */
export function useUserLinks(userId: string | undefined): UserLinks {
    const database = useRxDatabase();
    const [found, setFound] = useState<{
        userId: string;
        rows: UserLinkRow[];
    } | null>(null);

    useEffect(() => {
        if (!database || !userId) {
            return;
        }
        let current = true;
        const collections = (
            database as unknown as { collections: Collections }
        ).collections;
        void readLinks(collections, userId).then((rows) => {
            if (current) {
                setFound({ userId, rows });
            }
        });
        return () => {
            current = false;
        };
    }, [database, userId]);

    const ready = found !== null && found.userId === userId;
    return { rows: ready ? found.rows : [], ready };
}

/**
 * Turns a record into its link row: the label the change log gives it (a
 * Planum/Profil under its Befund's number), its project and day, and the route
 * to it.
 */
export function linkRowBuilder(
    /** Befund id -> Befundnummer, for the Plana and Profile among the records. */
    findingNumber: ReadonlyMap<string, string>,
    /** Project id -> what names it; may be empty where one project is shown. */
    projectName: ReadonlyMap<string, string>
): (entityType: string, doc: LinkedDoc, kindId: string) => UserLinkRow {
    /** A Planum/Profil the way the app names one in passing: "Bef. 7000, Pl. 1". */
    const labelOf = (entityType: string, doc: LinkedDoc): string => {
        const finding = findingNumber.get(String(doc.findingId ?? ''));
        if (finding !== undefined && entityType === 'findingPlanum') {
            return planumRefLabel(
                finding,
                doc as unknown as FindingPlanumDocType
            );
        }
        if (finding !== undefined && entityType === 'findingProfile') {
            return profileRefLabel(
                finding,
                doc as unknown as FindingProfileDocType
            );
        }
        // A record named by its day alone (a Tagebuch) has no text label:
        // its day, German like every date, is the name.
        return historyLabel(entityType, doc) || formatDate(dayOf(doc));
    };

    return (entityType, doc, kindId) => {
        const projectId =
            entityType === 'project' ? doc.id : String(doc.projectId ?? '');
        return {
            id: `${entityType}:${doc.id}:${kindId}`,
            entityType,
            recordId: doc.id,
            kindId,
            label: labelOf(entityType, doc),
            project: projectName.get(projectId) ?? '',
            day: dayOf(doc),
            path: historyRecordPath(entityType, {
                id: doc.id,
                projectId: String(doc.projectId ?? ''),
                findingId: String(doc.findingId ?? '')
            })
        };
    };
}
